import React from 'react'
import { Session as SupaSession, User as SupaUser } from '@supabase/supabase-js'

import { ensureStripeCustomer } from '@/features/stripe-payments/api/ensure-stripe-customer'
import { getProfile, GetProfileResult } from '@/features/settings/api/get-profile'
import { fetchSubscriptionStatus } from '@/features/subscription/api/get-subscription-status'
import { subscriptionKeys } from '@/features/subscription/api/keys'
// import * as persisted from '../persisted';
import {
  getUser,
  impersonateWithToken,
  loginWithEmailAndPassword,
  logout,
  registerWithEmailAndPassword,
  resetPassword,
  updateEmail,
  updatePassword,
} from '@/lib/auth'
import { queryClient } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'
import { IS_PROD } from '@/env'

export type AuthenticatedUser = {
  session: SupaSession
  profile: GetProfileResult
  supaUser: SupaUser
}

export type AuthState =
  | { isAuthenticated: false; isLoading: boolean; isProvisioning: false }
  | { isAuthenticated: true; isLoading: false; isProvisioning: boolean; user: AuthenticatedUser }

export type AuthApiContext = {
  login: typeof loginWithEmailAndPassword
  registerWithEmailAndPassword: typeof registerWithEmailAndPassword
  logout: typeof logout
  resetPassword: typeof resetPassword
  updatePassword: typeof updatePassword
  updateEmail: typeof updateEmail
  getUser: typeof getUser
  refreshProfile: () => Promise<void>
  impersonateWithToken: typeof impersonateWithToken
}

// Contexts
const AuthStateContext = React.createContext<AuthState>({
  isAuthenticated: false,
  isLoading: true,
  isProvisioning: false,
})
const AuthApiContext = React.createContext<AuthApiContext>({
  login: loginWithEmailAndPassword,
  registerWithEmailAndPassword,
  logout,
  resetPassword,
  updatePassword,
  getUser,
  updateEmail,
  refreshProfile: async () => {},
  impersonateWithToken,
})

// Provider
export function Provider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = React.useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    isProvisioning: false,
  })

  // Helper function to load complete user data
  const loadUserData = React.useCallback(async (session: SupaSession): Promise<AuthenticatedUser | null> => {
    try {
      if (!session.user) return null

      const profile = await getProfile({ id: session.user.id })
      if (!profile) {
        console.error('No profile found for user:', session.user.id)
        return null
      }

      return {
        session,
        profile,
        supaUser: session.user,
      }
    } catch (error) {
      console.error('Error loading user data:', error)
      return null
    }
  }, [])

  // Helper function to refresh profile data
  const refreshProfile = React.useCallback(async () => {
    if (!authState.isAuthenticated) return

    try {
      const updatedProfile = await getProfile({ id: authState.user.supaUser.id })
      if (updatedProfile) {
        setAuthState((prev) => {
          if (!prev.isAuthenticated) return prev
          return {
            ...prev,
            user: {
              ...prev.user,
              profile: updatedProfile,
            },
          }
        })

        // Also invalidate queries to keep react-query in sync
        queryClient.invalidateQueries({ queryKey: ['profile', authState.user.supaUser.id] })
      }
    } catch (error) {
      console.error('Error refreshing profile:', error)
    }
  }, [authState])

  // Fix for Supabase firing 'SIGNED_IN' event on every tab focus
  // Track current session to prevent unnecessary re-renders
  const currentSessionRef = React.useRef<{ userId: string | null }>({ userId: null })
  const isSignificantAuthChange = React.useCallback(
    (event: string, session: SupaSession | null, previousSession: typeof currentSessionRef.current): boolean => {
      if (event === 'SIGNED_OUT' || event === 'PASSWORD_RECOVERY' || event === 'USER_UPDATED') {
        return true
      }

      if (event === 'INITIAL_SESSION') {
        return !previousSession.userId
      }

      // SIGNED_IN -> tab focus issue
      if (event === 'SIGNED_IN') {
        if (!session?.user) {
          // Session became null - this is a real sign out
          return previousSession.userId !== null
        }

        const newUserId = session.user.id

        // Check if user actually changed (different account)
        if (newUserId !== previousSession.userId) {
          return true
        }

        return false
      }

      return true
    },
    [],
  )

  // Update session ref when state changes
  React.useEffect(() => {
    if (authState.isAuthenticated) {
      currentSessionRef.current = {
        userId: authState.user.supaUser.id,
      }
    } else {
      currentSessionRef.current = {
        userId: null,
      }
    }
  }, [authState])

  React.useEffect(() => {
    let mounted = true

    // Initialize session
    const initializeAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (!mounted) return

        if (session) {
          const userData = await loadUserData(session)
          if (userData && mounted) {
            setAuthState({
              isAuthenticated: true,
              isLoading: false,
              isProvisioning: false,
              user: userData,
            })
          } else if (mounted) {
            setAuthState({ isAuthenticated: false, isLoading: false, isProvisioning: false })
          }
        } else if (mounted) {
          setAuthState({ isAuthenticated: false, isLoading: false, isProvisioning: false })
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        if (mounted) {
          setAuthState({ isAuthenticated: false, isLoading: false, isProvisioning: false })
        }
      }
    }

    initializeAuth()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, _session) => {
      if (!mounted) return

      if (!isSignificantAuthChange(_event, _session, currentSessionRef.current)) {
        return
      }

      switch (_event) {
        case 'INITIAL_SESSION':
        case 'SIGNED_IN': {
          if (_session) {
            setAuthState({ isAuthenticated: false, isLoading: true, isProvisioning: false })
            // HACK: defer user data loading to avoid blocking UI
            setTimeout(async () => {
              const userData = await loadUserData(_session) // this causes a deadlock as it calls another supabase function
              if (userData && mounted) {
                setAuthState({
                  isAuthenticated: true,
                  isLoading: false,
                  isProvisioning: false,
                  user: userData,
                })
              } else if (mounted) {
                setAuthState({ isAuthenticated: false, isLoading: false, isProvisioning: false })
              }
            }, 0)
          } else if (mounted) {
            queryClient.clear()
            setAuthState({ isAuthenticated: false, isLoading: false, isProvisioning: false })
          }
          break
        }
        case 'USER_UPDATED': {
          if (_session && mounted) {
            // Refresh user data when user is updated
            const userData = await loadUserData(_session)
            if (userData) {
              setAuthState({
                isAuthenticated: true,
                isLoading: false,
                isProvisioning: false,
                user: userData,
              })
            }
          }
          break
        }
        case 'PASSWORD_RECOVERY': {
          // Handle password recovery if needed
          break
        }
        case 'SIGNED_OUT': {
          if (mounted) {
            queryClient.clear()
            setAuthState({ isAuthenticated: false, isLoading: false, isProvisioning: false })
          }
          break
        }
        default:
          break
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [loadUserData, isSignificantAuthChange])

  const api: AuthApiContext = {
    login: loginWithEmailAndPassword,
    registerWithEmailAndPassword,
    logout,
    resetPassword,
    updatePassword,
    updateEmail,
    getUser,
    refreshProfile,
    impersonateWithToken,
  }

  React.useEffect(() => {
    if (!authState.isAuthenticated) return

    let cancelled = false
    const userId = authState.user.profile.id

    const provisionSession = async () => {
      setAuthState((prev) => (prev.isAuthenticated ? { ...prev, isProvisioning: true } : prev))

      try {
        if (IS_PROD) {
          await ensureStripeCustomer()
        }
      } catch (error) {
        logger.error('Failed to ensure Stripe customer', { error })
      }

      if (cancelled) {
        setAuthState((prev) => (prev.isAuthenticated ? { ...prev, isProvisioning: false } : prev))
        return
      }

      try {
        await queryClient.prefetchQuery({
          queryKey: subscriptionKeys.status(userId),
          queryFn: fetchSubscriptionStatus,
        })
      } catch (error) {
        logger.error('Failed to prefetch subscription status', { error })
      } finally {
        if (!cancelled) {
          setAuthState((prev) => (prev.isAuthenticated ? { ...prev, isProvisioning: false } : prev))
        }
      }
    }

    provisionSession()

    return () => {
      cancelled = true
    }
  }, [authState.isAuthenticated, authState.isAuthenticated ? authState.user.profile.id : null])

  return (
    <AuthStateContext.Provider value={authState}>
      <AuthApiContext.Provider value={api}>{children}</AuthApiContext.Provider>
    </AuthStateContext.Provider>
  )
}

// Hook functions
export function useAuth() {
  return React.useContext(AuthStateContext)
}

export function useAuthenticatedUser(): AuthenticatedUser {
  const authState = React.useContext(AuthStateContext)

  if (!authState.isAuthenticated) {
    throw new Error(
      'useAuthenticatedUser can only be used when user is authenticated. Use useAuth to check authentication state first.',
    )
  }

  return authState.user
}

export function useAuthApi() {
  return React.useContext(AuthApiContext)
}

// Utility hooks for common patterns
export function useIsAuthenticated(): boolean {
  const authState = useAuth()
  return authState.isAuthenticated
}

export function useIsLoading(): boolean {
  const authState = useAuth()
  return authState.isLoading
}

/**
 * @deprecated use `useAuthenticatedUser()` or `useAuth()` instead
 * @returns
 */
export function useSession() {
  const authState = useAuth()

  // Return old API shape for backward compatibility
  if (authState.isAuthenticated) {
    return {
      session: authState.user.session,
      user: authState.user.profile,
    }
  }

  return null
}

export const useSessionApi = useAuthApi
